import React from "react";
import { RestaurantTable } from "@/redux/services/table/table.type";
import { displayPrice, parseMoneyToCents } from "@/lib/utils";
import { Bike, MapPin, ShoppingCart, StickyNote, Users, X } from "lucide-react";
import CartItem from "./CartItem";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CartEntry, DraftOrder, ORDER_TYPE_LABELS } from "./types";

interface CartPanelProps {
  order: DraftOrder | null;
  cart: CartEntry[];
  currency: string;
  cartTotal: number;
  cartItemsCount: number;
  tables: RestaurantTable[];
  onUpdateQuantity: (entryId: string, delta: number) => void;
  onRemoveItem: (entryId: string) => void;
  onClose: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  onUpdateOrderConfig: (
    orderId: string,
    updates: Partial<
      Pick<
        DraftOrder,
        | "tableId"
        | "notes"
        | "modificationReason"
        | "deliveryAddress"
        | "deliveryFee"
        | "deliveryPersonName"
        | "deliveryPersonPhone"
        | "guestCount"
      >
    >
  ) => void;
  onDeleteOrder: (orderId: string) => void;
  /** Optional banner shown above the cart in edit mode (e.g. inventory warning). */
  editWarning?: string | null;
  /** Reference label for the order being edited (e.g. "0042" or daily seq). */
  editOrderLabel?: string;
  /**
   * Add-only edit (ready/served order): existing lines are locked — they can't
   * be removed or reduced below their committed quantity.
   */
  addOnly?: boolean;
  /** Show the dine-in guest-count field (RestaurantSettings.shouldAskForGuestCount). */
  askGuestCount?: boolean;
}

const CartPanel: React.FC<CartPanelProps> = ({
  order,
  cart,
  currency,
  cartTotal,
  cartItemsCount,
  tables,
  onUpdateQuantity,
  onRemoveItem,
  onClose,
  onSubmit,
  isSubmitting,
  onUpdateOrderConfig,
  onDeleteOrder,
  editWarning,
  editOrderLabel,
  addOnly = false,
  askGuestCount = false,
}) => {
  const isEditMode = !!order?.editingOrderId;
  if (!order) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-bold text-lg text-card-foreground">Commande</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2 px-5">
          <ShoppingCart size={32} strokeWidth={1.5} />
          <p className="text-sm text-center">
            Aucune commande active. Créez une nouvelle commande pour commencer.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div>
          <h2 className="font-bold text-lg text-card-foreground">
            {isEditMode
              ? `Modifier la commande${editOrderLabel ? ` #${editOrderLabel}` : ""}`
              : "Commande en cours"}
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {ORDER_TYPE_LABELS[order.orderType]}
            {order.orderType === "dine_in" &&
              order.tableId &&
              ` · Table ${tables.find((t) => t.id === order.tableId)?.code ?? ""}`}
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-muted transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        <div className="space-y-4">
          {/* Inventory warning when editing an order already in preparation */}
          {isEditMode && editWarning && (
            <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning">
              {editWarning}
            </div>
          )}

          {/* Table select — only for dine_in */}
          {order.orderType === "dine_in" && tables.length > 0 && (
            <Select
              value={order.tableId ?? ""}
              onValueChange={(value) =>
                onUpdateOrderConfig(order.id, { tableId: value || undefined })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner une table" />
              </SelectTrigger>
              <SelectContent>
                {tables.map((table) => (
                  <SelectItem key={table.id} value={table.id}>
                    Table {table.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Guest count — dine-in only, edit mode (create collects it in the
              confirm modal), when the restaurant asks for it */}
          {isEditMode && askGuestCount && order.orderType === "dine_in" && (
            <div className="space-y-1.5">
              <label
                htmlFor={`guest-count-${order.id}`}
                className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground"
              >
                <Users className="h-4 w-4" />
                Nombre de couverts{" "}
                <span className="text-xs font-normal">(optionnel)</span>
              </label>
              <input
                id={`guest-count-${order.id}`}
                type="number"
                min={1}
                inputMode="numeric"
                value={order.guestCount ?? ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  const parsed = parseInt(raw, 10);
                  onUpdateOrderConfig(order.id, {
                    guestCount:
                      raw !== "" && Number.isFinite(parsed) && parsed > 0
                        ? parsed
                        : undefined,
                  });
                }}
                placeholder="Ex. 4"
                className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm text-card-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          )}

          {/* Delivery details — editable inline in edit mode for delivery orders */}
          {isEditMode && order.orderType === "delivery" && (
            <div className="space-y-3 rounded-md border border-border bg-muted/30 px-3 py-3">
              <p className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
                <MapPin className="h-4 w-4" />
                Livraison
              </p>
              <input
                type="text"
                value={order.deliveryAddress ?? ""}
                onChange={(e) =>
                  onUpdateOrderConfig(order.id, {
                    deliveryAddress: e.target.value,
                  })
                }
                placeholder="Adresse de livraison"
                className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm text-card-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <input
                type="number"
                inputMode="decimal"
                value={
                  order.deliveryFee != null ? String(order.deliveryFee / 100) : ""
                }
                onChange={(e) => {
                  const cents = parseMoneyToCents(e.target.value);
                  onUpdateOrderConfig(order.id, {
                    deliveryFee:
                      cents !== null && cents >= 0 ? cents : undefined,
                  });
                }}
                placeholder="Frais de livraison"
                className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm text-card-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />

              <p className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground pt-1">
                <Bike className="h-4 w-4" />
                Livreur{" "}
                <span className="text-xs font-normal">(optionnel)</span>
              </p>
              <input
                type="text"
                value={order.deliveryPersonName ?? ""}
                onChange={(e) =>
                  onUpdateOrderConfig(order.id, {
                    deliveryPersonName: e.target.value,
                  })
                }
                placeholder="Nom du livreur"
                className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm text-card-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <input
                type="tel"
                value={order.deliveryPersonPhone ?? ""}
                onChange={(e) =>
                  onUpdateOrderConfig(order.id, {
                    deliveryPersonPhone: e.target.value,
                  })
                }
                placeholder="Téléphone du livreur"
                className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm text-card-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          )}

          {/* Order-level note */}
          <div className="space-y-1.5">
            <label
              htmlFor={`order-notes-${order.id}`}
              className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground"
            >
              <StickyNote className="h-4 w-4" />
              Note pour la cuisine{" "}
              <span className="text-xs font-normal">(optionnel)</span>
            </label>
            <textarea
              id={`order-notes-${order.id}`}
              rows={2}
              value={order.notes ?? ""}
              onChange={(e) =>
                onUpdateOrderConfig(order.id, { notes: e.target.value })
              }
              placeholder="Ex. Allergique aux noix, servir sans glace…"
              className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm text-card-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
            />
          </div>

          {/* Edit reason — optional, edit mode only */}
          {isEditMode && (
            <div className="space-y-1.5">
              <label
                htmlFor={`order-edit-reason-${order.id}`}
                className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground"
              >
                <StickyNote className="h-4 w-4" />
                Raison de la modification{" "}
                <span className="text-xs font-normal">(optionnel)</span>
              </label>
              <input
                id={`order-edit-reason-${order.id}`}
                type="text"
                value={order.modificationReason ?? ""}
                onChange={(e) =>
                  onUpdateOrderConfig(order.id, {
                    modificationReason: e.target.value,
                  })
                }
                placeholder="Ex. Le client a changé de plat"
                className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm text-card-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          )}

          {/* Dashed separator */}
          <div className="border-t border-dashed border-border" />

          {/* Articles section */}
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground gap-2">
              <ShoppingCart size={32} strokeWidth={1.5} />
              <p className="text-sm text-center">
                Sélectionnez des articles dans le menu pour les ajouter à la
                commande.
              </p>
            </div>
          ) : (
            <div className="space-y-0">
              <h3 className="text-sm font-semibold text-card-foreground mb-3">
                Articles ({cartItemsCount})
              </h3>

              {cart.map((entry, index) => (
                <React.Fragment key={entry.id}>
                  {index > 0 && (
                    <div className="border-t border-border border-gray-100" />
                  )}
                  <CartItem
                    entry={entry}
                    currency={currency}
                    onUpdateQuantity={onUpdateQuantity}
                    onRemoveItem={onRemoveItem}
                    addOnly={addOnly}
                  />
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Dashed separator + Total */}
          {cart.length > 0 && (
            <>
              <div className="border-t border-dashed border-border" />
              <div className="flex justify-between items-center">
                <span className="text-xl font-medium">Total</span>
                <span className="text-xl font-bold text-card-foreground">
                  {displayPrice(cartTotal, currency)}
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-4 border-t border-border space-y-2">
        {cart.length > 0 && (
          <Button
            onClick={onSubmit}
            isLoading={isSubmitting}
            className="w-full"
            size="lg"
          >
            {isEditMode
              ? "Mettre à jour la commande"
              : "Confirmer la commande"}
          </Button>
        )}

        <button
          onClick={() => onDeleteOrder(order.id)}
          className="w-full py-2 text-sm text-destructive hover:text-destructive/80 transition-colors"
        >
          {isEditMode
            ? "Annuler les modifications"
            : "Annuler cette commande"}
        </button>
      </div>
    </div>
  );
};

export default CartPanel;
